import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { cn } from '@/lib/utils';

export interface QueueStatsStripProps {
  slug: string;
  className?: string;
}

interface StatsResponse {
  open_count: number;
  today_actions: number;
  last_activity: string | null;
}

const REFETCH_MS = 30_000;

/**
 * Top-of-page strip with three live counters (Open, Today's actions, Last
 * activity). Reads `/api/automations/<slug>/stats`. Query key is
 * `['stats', slug]` — frozen so W1-B's Refresh button and W2-C's mutations
 * can invalidate it.
 */
export function QueueStatsStrip({ slug, className }: QueueStatsStripProps) {
  const { data, isLoading, isError } = useQuery<StatsResponse>({
    queryKey: ['stats', slug],
    queryFn: async () => {
      const res = await apiClient.get(`/automations/${slug}/stats`);
      return res.data as StatsResponse;
    },
    enabled: !!slug,
    refetchInterval: REFETCH_MS,
  });

  const showLoading = isLoading;
  const showError = !isLoading && isError;

  const openValue = showLoading ? '…' : showError ? '—' : String(data?.open_count ?? 0);
  const todayValue = showLoading
    ? '…'
    : showError
      ? '—'
      : String(data?.today_actions ?? 0);
  const lastValue = showLoading
    ? '…'
    : showError
      ? '—'
      : formatRelative(data?.last_activity ?? null);

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-24 gap-y-8 border-b border-neutral-200 px-16 py-10 text-sm laptop:px-24',
        className,
      )}
      role="status"
      aria-live="polite"
      aria-label="Queue stats"
    >
      <Stat label="Open" value={openValue} emphasize />
      <Divider />
      <Stat label="Today's actions" value={todayValue} emphasize />
      <Divider />
      <Stat label="Last activity" value={lastValue} />
    </div>
  );
}

interface StatProps {
  label: string;
  value: string;
  emphasize?: boolean;
}

function Stat({ label, value, emphasize }: StatProps) {
  return (
    <div className="flex items-baseline gap-8">
      <span className="text-xs uppercase tracking-wide text-neutral-500">{label}</span>
      <span
        className={cn(
          emphasize
            ? 'text-base font-semibold text-neutral-900'
            : 'text-sm text-neutral-700',
        )}
      >
        {value}
      </span>
    </div>
  );
}

function Divider() {
  return (
    <span aria-hidden="true" className="h-12 w-px bg-neutral-200" />
  );
}

/**
 * Lightweight relative-time formatter. Avoids pulling in date-fns for one
 * call site. Returns "—" for null/invalid input, "Just now" under a minute,
 * "5 minutes ago", "2 hours ago", "Yesterday at 3:14 pm", or the absolute
 * date for anything older than ~6 days.
 */
function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '—';
  const now = Date.now();
  const diffSec = Math.max(0, Math.round((now - ts) / 1000));

  if (diffSec < 45) return 'Just now';
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} ${diffMin === 1 ? 'minute' : 'minutes'} ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} ${diffHr === 1 ? 'hour' : 'hours'} ago`;

  const then = new Date(ts);
  const today = new Date(now);
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startOfThen = new Date(then.getFullYear(), then.getMonth(), then.getDate());
  const dayDiff = Math.round((startOfToday.getTime() - startOfThen.getTime()) / 86_400_000);

  const timeFmt = then.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });

  if (dayDiff === 1) return `Yesterday at ${timeFmt.toLowerCase()}`;
  if (dayDiff < 7) {
    const weekday = then.toLocaleDateString(undefined, { weekday: 'long' });
    return `${weekday} at ${timeFmt.toLowerCase()}`;
  }
  return then.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: today.getFullYear() === then.getFullYear() ? undefined : 'numeric',
  });
}
